/**
 * Spec 035 v0.4, step 3: how a unit's head is launched — by hand, by itself when work comes, or
 * on a schedule (Yekaterinburg) — on the person's own PC with the «Агенты» app, and «Запустить
 * подразделение», which always works. A PC that is off behaves as «вручную»; the unit says why.
 */

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import { Devices } from "@/api/resources";
import { UnitLaunches, type DepartmentOut, type LaunchMode, type LaunchOut } from "@/api/units";
import { Button } from "@/components/ui/Button";
import { SelectField, TextField } from "@/components/ui/Field";
import { Segmented } from "@/components/ui/Segmented";
import { Sheet } from "@/components/ui/Sheet";
import { useToast } from "@/components/ui/Toast";
import { formatRelativePast } from "@/lib/format";
import { LAUNCH_MODE_LABEL, launchLine, launchResultsLine, parseTimes } from "@/lib/units";

const MODES: { value: LaunchMode; label: string }[] = [
  { value: "manual", label: "Вручную" },
  { value: "auto", label: "Сам" },
  { value: "schedule", label: "По расписанию" },
];

function useMyDevices(enabled = true) {
  const devices = useQuery({ queryKey: ["devices"], queryFn: Devices.list, enabled });
  return (devices.data ?? []).filter((d) => d.mine && !d.revoked_at);
}

function LaunchSheet({ unit, launch, onClose }: { unit: DepartmentOut; launch: LaunchOut; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const mine = useMyDevices();
  const [mode, setMode] = useState<LaunchMode>(launch.mode);
  const [device, setDevice] = useState(launch.device?.id ?? "");
  const [times, setTimes] = useState(launch.times.join(", "));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const parsed = parseTimes(times);
  const ready = mode === "manual" || (device && (mode === "auto" || (parsed && parsed.length > 0)));

  const save = async () => {
    setError(undefined);
    setSaving(true);
    try {
      await UnitLaunches.set(unit.id, {
        mode,
        times: mode === "schedule" ? parsed ?? [] : [],
        device_id: device || null,
        version: launch.version || null,
      });
      void qc.invalidateQueries({ queryKey: ["departments"] });
      toast.success(`«${unit.name}»: ${LAUNCH_MODE_LABEL[mode].toLowerCase()}`);
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) void qc.invalidateQueries({ queryKey: ["departments", "launch", unit.id] });
      setError(e instanceof Error ? e.message : "Не сохранилось");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open onClose={onClose} title={`Как запускать «${unit.name}»`}
      footer={
        <div className="space-y-2">
          {error && <p role="alert" className="text-meta text-p-blocked">{error}</p>}
          <Button block variant="strong" loading={saving} loadingText="Сохраняем…" disabled={!ready} onClick={() => void save()}>
            Сохранить
          </Button>
        </div>
      }>
      <div className="flex flex-col gap-3">
        <Segmented label="Руководитель и его сотрудники" options={MODES} value={mode} onChange={setMode} />
        <p className="text-meta text-p-text-muted">
          {mode === "manual" && "Работа ждёт, пока кто-то нажмёт «Запустить»; утром придёт напоминание."}
          {mode === "auto" && "Когда приходит работа, приложение «Агенты» на ПК запускает агента само. ПК выключен — работа ждёт, как вручную."}
          {mode === "schedule" && "Каждый день в эти часы (Екатеринбург) запускаются руководитель и сотрудники, у которых есть работа."}
        </p>
        {mode !== "manual" && (
          <SelectField
            label="На каком ПК"
            hint="Только свой: агента запускают от имени владельца ПК, его версия должна быть одобрена тобой"
            value={device}
            placeholder={mine.length ? "Выбери ПК" : "Своих подключённых ПК нет"}
            options={mine.map((d) => ({ value: d.id, label: `${d.name}${d.online ? "" : " — не на связи"}` }))}
            onChange={setDevice}
          />
        )}
        {mode === "schedule" && (
          <TextField label="Во сколько" hint="Например: 9:00, 18:30" value={times} maxLength={60}
            error={times.trim() && !parsed ? "Время — в виде 09:00" : undefined}
            onChange={(e) => setTimes(e.target.value)} />
        )}
      </div>
    </Sheet>
  );
}

/** The unit's launch mode, why the last automatic launch did not go, and «Запустить». */
export function LaunchBlock({ unit }: { unit: DepartmentOut }) {
  const qc = useQueryClient();
  const toast = useToast();
  const launch = useQuery({ queryKey: ["departments", "launch", unit.id], queryFn: () => UnitLaunches.get(unit.id) });
  const mine = useMyDevices();
  const [editing, setEditing] = useState(false);
  const [starting, setStarting] = useState(false);
  const data = launch.data;
  if (!data) return null;
  const unitPc = data.device && mine.some((d) => d.id === data.device!.id) ? data.device.id : null;
  const pc = unitPc ?? mine.find((d) => d.online)?.id ?? null;

  const start = async () => {
    setStarting(true);
    try {
      const results = await UnitLaunches.now(unit.id, pc);
      toast.success(launchResultsLine(results));
      void qc.invalidateQueries({ queryKey: ["departments"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не запустилось");
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-card border border-p-border bg-p-surface px-4 py-3">
      <p className="text-meta text-p-text">{launchLine(data)}</p>
      {data.note && (
        <p className="text-meta text-p-needs">
          Не запустился{data.note_at ? ` ${formatRelativePast(new Date(data.note_at))}` : ""}: {data.note}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="compact" variant="secondary" onClick={() => setEditing(true)}>Как запускать</Button>
        <Button size="compact" variant="strong" loading={starting} loadingText="Запускаем…" disabled={!pc}
          onClick={() => void start()}>
          Запустить подразделение
        </Button>
      </div>
      {!pc && (
        <p className="text-meta text-p-text-muted">
          Своего ПК на связи нет: подключи его в «Отделах» → «Компьютеры» или скачай папку в карточке агента.
        </p>
      )}
      {editing && <LaunchSheet unit={unit} launch={data} onClose={() => setEditing(false)} />}
    </div>
  );
}
